import { PLATFORM_TENANT_SLUG } from "@/constants/platform";
import { LOGIN_REDIRECT_BY_ROLE, ROUTES } from "@/constants/routes";
import type { AuthUser, UserRole } from "@/types";
import {
  getGlobalSiteOrigin,
  getTenantSiteOrigin,
  resolveTenantFromHost,
} from "./tenant";
import { canAccessRoute } from "./permissions";

const BASE_DOMAIN =
  import.meta.env.VITE_BASE_DOMAIN ?? "tercerespacio.com.ar";

export type BackofficeArea = "admin" | "moderar" | "manager";

export interface PostLoginHostContext {
  hostname: string;
  isTenantSite: boolean;
  hostSlug: string | null;
  effectiveTenantSlug: string;
}

/**
 * Resolves the business tenant slug for the current host.
 * www and localhost without dev slug map to the main-tenant (platform).
 */
export const resolveEffectiveTenantSlug = (
  hostname: string = window.location.hostname,
): string => {
  const hostSlug = resolveTenantFromHost(hostname, BASE_DOMAIN)?.slug ?? null;
  return hostSlug ?? PLATFORM_TENANT_SLUG;
};

/**
 * Resolves the active backoffice area from the pathname prefix.
 */
export const resolveBackofficeArea = (pathname: string): BackofficeArea => {
  if (pathname.startsWith(ROUTES.backoffice.manager)) {
    return "manager";
  }
  if (pathname.startsWith(ROUTES.backoffice.moderar)) {
    return "moderar";
  }
  return "admin";
};

/**
 * Resolves X-Tenant-Slug for authenticated /tenants/me/* backoffice requests.
 * ADMIN always uses effectiveTenantSlug (host-derived); apiClient only sends
 * the header on /tenants/me/* endpoints. MANAGER omits it (JWT tenantId).
 */
export function resolveBackofficeTenantScopeForMe(
  _pathname: string,
  role: UserRole | undefined,
  effectiveTenantSlug: string,
): string | null {
  if (role === "ADMIN") {
    return effectiveTenantSlug;
  }

  return null;
}

/**
 * Whether the effective tenant is the main-tenant (platform).
 */
export const isMainTenantSlug = (slug: string): boolean =>
  slug === PLATFORM_TENANT_SLUG;

/**
 * Whether the current host represents the main-tenant.
 */
export const isMainTenantHost = (
  hostname: string = window.location.hostname,
): boolean => isMainTenantSlug(resolveEffectiveTenantSlug(hostname));

/**
 * Origin for manager backoffice on the given tenant slug.
 */
export const getManagerOriginForTenantSlug = (slug: string): string =>
  isMainTenantSlug(slug)
    ? getGlobalSiteOrigin()
    : getTenantSiteOrigin(slug);

/**
 * Absolute URL to the public landing of a tenant.
 */
export const getTenantLandingUrl = (effectiveTenantSlug: string): string => {
  const origin = isMainTenantSlug(effectiveTenantSlug)
    ? getGlobalSiteOrigin()
    : getTenantSiteOrigin(effectiveTenantSlug);

  return `${origin}${ROUTES.public.home}`;
};

/**
 * Home path for a backoffice area on the correct origin.
 */
export const getBackofficeAreaHome = (
  area: BackofficeArea,
  effectiveTenantSlug?: string,
): string => {
  switch (area) {
    case "admin":
      return ROUTES.backoffice.admin;
    case "moderar":
      return ROUTES.backoffice.moderar;
    case "manager": {
      const slug = effectiveTenantSlug ?? resolveEffectiveTenantSlug();
      return `${getManagerOriginForTenantSlug(slug)}${ROUTES.backoffice.manager}`;
    }
  }
};

/**
 * Home link for the active backoffice area (path or absolute URL).
 */
export const getBackofficeAreaHomeLink = (
  area: BackofficeArea,
  effectiveTenantSlug: string,
): { href: string; external: boolean } => {
  const full = getBackofficeAreaHome(area, effectiveTenantSlug);

  if (full.startsWith(window.location.origin)) {
    return {
      href: full.slice(window.location.origin.length) || ROUTES.public.home,
      external: false,
    };
  }

  return { href: full, external: true };
};

/**
 * Human-readable context label for the sidebar.
 */
export const getBackofficeContextLabel = (
  area: BackofficeArea,
  effectiveTenantSlug: string,
): string => {
  switch (area) {
    case "admin":
      return "Administración · plataforma";
    case "moderar":
      return "Moderación · plataforma";
    case "manager":
      return `Gestión · ${effectiveTenantSlug}`;
  }
};

export const buildPostLoginHostContext = (
  hostname: string = window.location.hostname,
): PostLoginHostContext => {
  const hostSlug = resolveTenantFromHost(hostname, BASE_DOMAIN)?.slug ?? null;
  const effectiveTenantSlug = hostSlug ?? PLATFORM_TENANT_SLUG;

  return {
    hostname,
    isTenantSite: hostSlug !== null,
    hostSlug,
    effectiveTenantSlug,
  };
};

const isGlobalBackofficePath = (pathname: string): boolean =>
  pathname.startsWith(ROUTES.backoffice.admin) ||
  pathname.startsWith(ROUTES.backoffice.moderar);

const sanitizeFromPath = (
  role: UserRole,
  fromPath: string | null,
): string | null => {
  if (!fromPath || !canAccessRoute(role, fromPath)) {
    return null;
  }

  if (role === "MODERATOR" && fromPath.startsWith(ROUTES.backoffice.manager)) {
    return null;
  }

  return fromPath;
};

const buildAbsoluteTarget = (origin: string, path: string): string => {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  return `${origin}${path.startsWith("/") ? path : `/${path}`}`;
};

const requiresCrossOrigin = (target: string): boolean => {
  try {
    const url = new URL(target, window.location.origin);
    return url.origin !== window.location.origin;
  } catch {
    return false;
  }
};

/**
 * Resolves post-login navigation target for the authenticated user.
 */
export const resolvePostLoginTarget = (
  authUser: AuthUser,
  fromPath: string | null,
  hostContext: PostLoginHostContext = buildPostLoginHostContext(),
): string => {
  const safeFromPath = sanitizeFromPath(authUser.role, fromPath);

  if (authUser.role === "MODERATOR") {
    if (hostContext.isTenantSite) {
      return buildAbsoluteTarget(
        getGlobalSiteOrigin(),
        safeFromPath ?? LOGIN_REDIRECT_BY_ROLE.MODERATOR,
      );
    }
    return safeFromPath ?? LOGIN_REDIRECT_BY_ROLE.MODERATOR;
  }

  if (authUser.role === "MANAGER") {
    if (!authUser.tenantSlug) {
      return LOGIN_REDIRECT_BY_ROLE.MANAGER;
    }

    if (authUser.tenantSlug === hostContext.effectiveTenantSlug) {
      return safeFromPath ?? LOGIN_REDIRECT_BY_ROLE.MANAGER;
    }

    return buildAbsoluteTarget(
      getManagerOriginForTenantSlug(authUser.tenantSlug),
      safeFromPath ?? LOGIN_REDIRECT_BY_ROLE.MANAGER,
    );
  }

  if (authUser.role === "ADMIN") {
    if (safeFromPath?.startsWith(ROUTES.backoffice.manager)) {
      const managerSlug = hostContext.effectiveTenantSlug;
      if (
        hostContext.isTenantSite ||
        isMainTenantSlug(managerSlug)
      ) {
        return safeFromPath;
      }
      return buildAbsoluteTarget(
        getManagerOriginForTenantSlug(managerSlug),
        safeFromPath,
      );
    }

    if (safeFromPath && isGlobalBackofficePath(safeFromPath)) {
      if (hostContext.isTenantSite) {
        return buildAbsoluteTarget(getGlobalSiteOrigin(), safeFromPath);
      }
      return safeFromPath;
    }

    if (hostContext.isTenantSite) {
      return buildAbsoluteTarget(
        getGlobalSiteOrigin(),
        safeFromPath ?? LOGIN_REDIRECT_BY_ROLE.ADMIN,
      );
    }

    return safeFromPath ?? LOGIN_REDIRECT_BY_ROLE.ADMIN;
  }

  return safeFromPath ?? ROUTES.backoffice.login;
};

/**
 * Navigates after login using replace when crossing origins.
 */
export const redirectAfterLogin = (
  authUser: AuthUser,
  fromPath: string | null,
  navigate: (to: string, options?: { replace?: boolean }) => void,
): void => {
  const target = resolvePostLoginTarget(authUser, fromPath);

  if (requiresCrossOrigin(target)) {
    window.location.replace(target);
    return;
  }

  const path = target.startsWith(window.location.origin)
    ? target.slice(window.location.origin.length)
    : target;

  navigate(path, { replace: true });
};
